import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, Package, Zap, Globe, Award, FileText, Settings } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

export const ServicesPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const services = [
    {
      icon: FileText,
      title: 'VTV Stationary',
      description: 'Complete range of office and school stationery supplies.',
      image: '/Services/VTV Stationery.png',
    },
    {
      icon: Package,
      title: 'VTV Fancy',
      description: 'Unique fancy items, gifts, and decorative products.',
      image: '/Services/VTV Fancy.png',
    },
    {
      icon: Zap,
      title: 'VTV Electronic',
      description: 'Quality electronic products and accessories.',
      image: '/Services/VTV Electronic.png',
    },
    {
      icon: Globe,
      title: 'VTV Travel',
      description: 'Comprehensive travel services and tour packages.',
      image: '/Services/VTV Travel.png',
    },
    {
      icon: Award,
      title: 'VTV Education',
      description: 'Educational materials and learning resources.',
      image: '/Services/VTV Education.png',
    },
    {
      icon: Settings,
      title: 'VTV Solutions',
      description: 'Tailored business solutions for your needs.',
      image: '/Services/VTV Solutions.png',
    },
  ];

  return (
    <section ref={ref} className="py-10 sm:py-14">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Our <span className="gradient-text-blue">Services</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Comprehensive solutions designed to meet your business needs with excellence and efficiency.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 20, scale: 0.95 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.03, rotateZ: 1 }}
                className="relative rounded-xl group cursor-pointer overflow-hidden h-80"
              >
                {/* Background Image */}
                <img
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

                {/* Content */}
                <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 space-y-3">
                  <h3 className="text-xl font-bold text-white group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-200 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12"
        >
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 glow-blue group">
            <Link to="/services" className="flex items-center gap-2">
              View All Services
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
